import {
  isSegmentTrackEvent,
  removeNullsAndTypeNames,
} from "tracking/utils/mapGraphqlToSegmentEvent"
import type { ItemType, ItemUpdate, TrackProductAddedEventType } from "types"
import { track } from "tracking/client"
import { isCurrencyCode } from "utils/isCurrencyCode"
import { getTrackingEventId } from "./trackingEventId"
import { trackAddToBasket } from "utils/gtm"

// Segment tracking for product added
const handleBasketUpdateTracking = (
  trackingEvents: TrackProductAddedEventType[],
  event_id: string,
  locationType?: string,
  locationTypeDetails?: string
) => {
  trackingEvents.forEach((trackingEvent) => {
    const sanitizedTrackingEvent = removeNullsAndTypeNames({
      ...trackingEvent,
      properties: {
        ...trackingEvent.properties,
        location_type: locationType,
        location_type_details: locationTypeDetails,
      },
    })

    if (isSegmentTrackEvent(sanitizedTrackingEvent)) {
      track(sanitizedTrackingEvent, { event_id })
    }
  })
}

// GTM tracking for product added
const handleProductAddedGTMTracking = ({
  trackingEvents,
  basketItems,
  itemUpdates,
  eventId,
}: {
  trackingEvents: TrackProductAddedEventType[]
  basketItems: ItemType[]
  itemUpdates: ItemUpdate[]
  eventId: string
}) => {
  const currency = trackingEvents?.[0]?.properties?.currency

  if (!isCurrencyCode(currency)) return

  const mappedItems = basketItems
    .filter((item) => itemUpdates.some((update) => update.id === item.id))
    .map((item) => ({
      id: item.id,
      origin: item.origin,
      quantity: item.quantity,
      type: item.type,
      ...(item.type === "membership"
        ? { membershipId: item.membership?.value ?? "" }
        : { variantSku: item.variant?.sku ?? "" }),
    }))

  trackAddToBasket(mappedItems, false, currency, eventId)
}

// Tracking for product added
export const handleProductAddedUpdateTracking = ({
  trackingEvents,
  basketItems,
  itemUpdates,
  locationType,
  locationTypeDetails,
}: {
  trackingEvents: TrackProductAddedEventType[]
  basketItems: ItemType[]
  itemUpdates: ItemUpdate[]
  locationType: string
  locationTypeDetails?: string
}) => {
  const eventId = getTrackingEventId()

  handleProductAddedGTMTracking({
    trackingEvents,
    basketItems,
    itemUpdates,
    eventId,
  })

  const productAddedTrackingEvents = trackingEvents.filter(
    ({ event }) => event === "Product Added"
  )

  handleBasketUpdateTracking(
    productAddedTrackingEvents,
    eventId,
    locationType,
    locationTypeDetails
  )
}
